import React, { useState, useEffect } from "react";
import {
    Box, Typography, Card, Switch, Button, Divider, Checkbox, 
    TextField, InputAdornment, Stack, Alert, Avatar, Grid, Snackbar, CircularProgress
} from "@mui/material";
import { LocalPharmacyOutlined as PharmacyIcon, LocalHospitalOutlined as DoctorIcon, NotificationsActive as BellIcon, ErrorOutline as InfoIcon } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import medicationService from "../services/medicationService";

// Reuse these colors to match settings design
const TEXT_MAIN = "#1A2B45";
const TEXT_SUB = "#5C6F85";
const CARD_BG = "#FFFFFF";
const BORDER = "#E2E8F0";

const RecipientRow = ({ icon, title, desc, checked, onChange, disabled }: any) => (
    <Box sx={{
        display: "flex", alignItems: "center", gap: 2,
        p: "14px 18px", borderRadius: "14px", cursor: disabled ? "default" : "pointer",
        border: `1.5px solid ${checked ? "#48CAE4" : BORDER}`,
        bgcolor: checked ? "#E8F8FB" : "#FAFBFC",
        transition: "all 0.2s",
        "&:hover": { borderColor: disabled ? (checked ? "#48CAE4" : BORDER) : "#48CAE4", boxShadow: disabled ? "none" : "0 4px 12px rgba(72,202,228,0.15)" },
        opacity: disabled ? 0.7 : 1,
    }} onClick={() => !disabled && onChange(!checked)}>
        <Checkbox 
            checked={checked} 
            disabled={disabled}
            onChange={(e) => { e.stopPropagation(); onChange(e.target.checked); }}
            sx={{ color: "#CBD5E1", "&.Mui-checked": { color: "#48CAE4" }, p: 0, '& .MuiSvgIcon-root': { fontSize: 24 } }} 
        />
        <Avatar sx={{ bgcolor: checked ? "#ffffff" : "#F1F5F9", color: checked ? "#0077B6" : "#64748B", width: 42, height: 42, borderRadius: "12px", border: checked ? "1px solid #48CAE440" : "1px solid transparent" }}>
            {icon}
        </Avatar>
        <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={800} sx={{ color: TEXT_MAIN, lineHeight: 1.3 }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: TEXT_SUB }}>{desc}</Typography>
        </Box>
    </Box>
);

const LowStockNotificationTab: React.FC = () => {
    const { user } = useAuth();

    // states
    const [enabled, setEnabled] = useState(true);
    const [notifyPharmacy, setNotifyPharmacy] = useState(true);
    const [notifyDoctor, setNotifyDoctor] = useState(true);
    const [daysBefore, setDaysBefore] = useState<number>(5);
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" as "success" | "error" });

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const data = await medicationService.getLowStockConfig();
                if (data) {
                    setEnabled(data.isEnabled !== false); 
                    setNotifyPharmacy(data.notifyPharmacy !== false);
                    setNotifyDoctor(data.notifyDoctor !== false);
                    setDaysBefore(data.reminderDays !== undefined ? data.reminderDays : 5);
                }
            } catch (err) {
                console.error("Failed to fetch low stock config", err);
            } finally {
                setLoading(false);
            }
        };
        fetchConfig();
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await medicationService.saveLowStockConfig({
                isEnabled: enabled,
                notifyPharmacy,
                notifyDoctor,
                reminderDays: daysBefore
            });
            setSnack({ open: true, msg: "Settings saved successfully!", sev: "success" });
        } catch (err) {
            console.error(err);
            setSnack({ open: true, msg: "Failed to save settings. Please try again.", sev: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
                <CircularProgress sx={{ color: "#48CAE4" }} />
            </Box>
        );
    }

    return (
        <>
        <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
            <Alert severity={snack.sev} sx={{ borderRadius: "12px", fontWeight: 600 }}>{snack.msg}</Alert>
        </Snackbar>

        <Card elevation={0} sx={{
            borderRadius: "20px", border: `1px solid ${BORDER}`, bgcolor: CARD_BG,
            overflow: "hidden", mb: 3, transition: "box-shadow 0.2s",
            "&:hover": { boxShadow: "0 8px 32px rgba(0,119,182,0.08)" },
        }}>
            {/* Header Area */}
            <Box sx={{
                px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2,
                borderBottom: `1px solid ${BORDER}`, bgcolor: enabled ? "#FAFBFC" : "#F1F5F9",
                justifyContent: "space-between", flexWrap: "wrap", transition: "background-color 0.3s"
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar sx={{ bgcolor: enabled ? "#E8F8FB" : "#E2E8F0", color: enabled ? "#48CAE4" : "#94A3B8", width: 42, height: 42, borderRadius: "12px", transition: "all 0.3s" }}>
                        <BellIcon />
                    </Avatar>
                    <Box>
                        <Typography fontWeight={800} sx={{ color: TEXT_MAIN, fontSize: "1rem", lineHeight: 1.2 }}>
                            Low Stock Email Notification
                        </Typography>
                        <Typography variant="caption" sx={{ color: TEXT_SUB }}>
                            Automated email alerts when medication stock is approaching its expected end date
                        </Typography>
                    </Box>
                </Box>
                <Switch
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#48CAE4" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#48CAE4" } }}
                />
            </Box>
            
            <Box sx={{ p: 4, opacity: enabled ? 1 : 0.4, pointerEvents: enabled ? "auto" : "none", transition: "opacity 0.3s" }}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1 }}>
                            <Typography fontWeight={800} sx={{ color: TEXT_MAIN, mb: 0.5, fontSize: "0.95rem" }}>
                                Notification Recipients
                            </Typography>
                            <Typography variant="caption" sx={{ color: TEXT_SUB }}>
                                Select who should receive low stock email alerts.
                            </Typography>
                        </Box>
                        
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <RecipientRow 
                                icon={<PharmacyIcon />}
                                title="Pharmacy Store"
                                desc="Send low stock alerts to the configured pharmacy."
                                checked={notifyPharmacy}
                                onChange={setNotifyPharmacy}
                            />
                            <RecipientRow 
                                icon={<DoctorIcon />}
                                title="Doctor"
                                desc="Send low stock alerts to the assigned doctor."
                                checked={notifyDoctor}
                                onChange={setNotifyDoctor}
                            />
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 1 }}>
                            <Typography fontWeight={800} sx={{ color: TEXT_MAIN, mb: 0.5, fontSize: "0.95rem" }}>
                                Alert Configuration
                            </Typography>
                            <Typography variant="caption" sx={{ color: TEXT_SUB }}>
                                Define when the notifications should begin sending.
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, mb: 3, p: "16px 20px", borderRadius: "14px", border: `1.5px solid ${BORDER}`, bgcolor: "#FAFBFC" }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={700} sx={{ color: TEXT_MAIN }}>Reminder Window</Typography>
                                <Typography variant="caption" sx={{ color: TEXT_SUB }}>Before expected runout</Typography>
                            </Box>
                            <TextField
                                size="small"
                                type="number"
                                value={daysBefore}
                                onChange={(e) => setDaysBefore(Number(e.target.value))}
                                InputProps={{ endAdornment: <InputAdornment position="end">Days</InputAdornment>, inputProps: { min: 1, max: 99 } }}
                                sx={{ width: 130, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff", fontWeight: 700 } }}
                            />
                        </Box>
                        
                        <Alert 
                            severity="info" 
                            icon={<InfoIcon sx={{ color: "#0077B6" }} />}
                            sx={{ 
                                borderRadius: "12px", border: "1px solid #BAE6FD", bgcolor: "#F0F9FF", color: "#0369A1", 
                                '& .MuiAlert-message': { pt: 0.5 }
                            }}
                        >
                            <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                The system will begin transmitting nightly emails precisely <b>{daysBefore} days</b> prior to the medication's expected end date.
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>
            </Box>

            {/* Action Buttons (Always active so users can save the "OFF" state) */}
            <Box sx={{ px: 4, pb: 4 }}>
                <Divider sx={{ mt: 0, mb: 3, borderColor: BORDER }} />
                
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                    <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#48CAE4", color: "#000000", "&:hover": { bgcolor: "#3ab3cc", boxShadow: "0 4px 12px rgba(72,202,228,0.3)" }, px: 4, minWidth: 140 }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : "Save Settings"}
                    </Button>
                </Box>
            </Box>
        </Card>
        </>
    );
};

export default LowStockNotificationTab;
